"use strict";
var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Parser = exports.parseFormula = void 0;
var node_1 = require("../ast/node");
var parseError_1 = __importDefault(require("./parseError"));
var binaryOperator_1 = require("../ast/binaryOperator");
/**
 * Parse the formula string using the given options into an abstract syntax tree.
 *
 * @param formula A string containing the formula to parse
 * @param options The options object to provide to the parser.
 * @see ParserOptions
 */
function parseFormula(formula, options) {
    if (options === void 0) { options = {}; }
    return new Parser(formula, options).parse();
}
exports.parseFormula = parseFormula;
/**
 * The Parser object parses a formula string into an abstract syntax tree.
 */
var Parser = /** @class */ (function () {
    /**
     * Constructs a new Parser with the given formula string and parsing options.
     *
     * @param input The formula string to parse.
     * @param options The options object for configuring the parser.
     */
    function Parser(input, options) {
        var _a;
        this.nodeStartIndices = [];
        this.currentIndex = 0;
        this.states = [];
        this.binaryOperatorFirstChars = ["^", "/", "*", "%", "+", "-", "<", ">", "="];
        this.input = input;
        this.parseArrayLiterals = (_a = options.parseArrayLiterals) !== null && _a !== void 0 ? _a : false;
    }
    /**
     * Parses this parser's input into an AST.
     *
     * The Parser instance must not be reused after calling {@link parse}.
     *
     * @returns The parsed {@link ASTNode} representing the formula or {@code null} if one could not be parsed.
     */
    Parser.prototype.parse = function () {
        if (!this.input.length)
            return null;
        return this.parseExpression();
    };
    Parser.prototype.inState = function (state) {
        if (this.states.length === 0) {
            return false;
        }
        for (var i = this.states.length - 1; i >= 0; i--) {
            if (this.states[i] === state) {
                return true;
            }
        }
        return false;
    };
    Parser.prototype.parseExpression = function () {
        var node = this.parseExpressionStart();
        // If a node is successfully parsed, try to parse whatever comes after it (if anything).
        if (node) {
            this.skipWhitespace();
            if (this.currentIndex < this.input.length) {
                var c = this.input[this.currentIndex];
                if (this.binaryOperatorFirstChars.includes(c)) {
                    node = this.parseBinaryOperator(node);
                }
                else if (c === ")" && (this.inState("group") || this.inState("function"))) {
                    // When we encounter a closing parenthesis while parsing inside of a group or function parameters
                    // we return the node and let parseGroup or parseStartingWithIdentifier consume the closing paren.
                    return node;
                }
                else if (c === "," && (this.inState("function") || this.inState("array"))) {
                    // Trailing commas are handled similarly to closing parens
                    return node;
                }
                else if (c === "]" && this.parseArrayLiterals && this.inState("array")) {
                    // And for array literals
                    return node;
                }
                else {
                    throw this.parseError("Unexpected character '" + c + "' after expression");
                }
            }
        }
        return node;
    };
    Parser.prototype.parseExpressionStart = function () {
        this.skipWhitespace();
        if (this.currentIndex >= this.input.length)
            return null;
        // Before we begin parsing this expression (but after we've skipped whitespace),
        // push the current index onto the start indices array, so that when this node is completed
        // we know the index at which it started.
        this.nodeStartIndices.push(this.currentIndex);
        var c = this.input[this.currentIndex];
        if (isDigit(c) || c === ".") {
            return this.parseNumber();
        }
        else if (isAlpha(c)) {
            return this.parseStartingWithIdentifier();
        }
        else if (c === "'" || c === "\"") {
            return this.parseString();
        }
        else if (c === "-") {
            return this.parseUnaryMinus();
        }
        else if (c === "(") {
            return this.parseGroup();
        }
        else if (c === "[") {
            if (this.parseArrayLiterals) {
                return this.parseArray();
            }
            else {
                throw this.parseError("Unexpected character '" + c + "' at start of expression. Support for array literals is experimental may be enabled by setting the parseArrayLiterals option to true on the parser.");
            }
        }
        else {
            throw this.parseError("Unexpected character '" + c + "' at start of expression");
        }
    };
    Parser.prototype.parseNumber = function () {
        var _this = this;
        if (!isDigit(this.input[this.currentIndex]) && this.input[this.currentIndex] !== ".")
            return null;
        var numberStr = '';
        var consumeDigits = function () {
            // Advance through the input, appending digits to numberStr until there are no more digits, or the input runs out.
            while (_this.currentIndex < _this.input.length && isDigit(_this.input[_this.currentIndex])) {
                numberStr += _this.input[_this.currentIndex];
                _this.currentIndex++;
            }
        };
        consumeDigits();
        // Check if floating point number and parse fractional component
        if (this.currentIndex < this.input.length && this.input[this.currentIndex] === '.') {
            this.currentIndex++;
            numberStr += '.';
            if (this.currentIndex >= this.input.length || !isDigit(this.input[this.currentIndex])) {
                // Parse error when there's a period in a number literal not followed by the fractional component
                throw this.parseError('Expected fractional component of number literal after decimal point');
            }
            else {
                consumeDigits();
            }
        }
        return this.create(node_1.NumberLiteralNode, parseFloat(numberStr));
    };
    Parser.prototype.parseStartingWithIdentifier = function () {
        if (!isAlpha(this.input[this.currentIndex]))
            return null;
        var isValidIdentifier = function (c) {
            return isAlphaNum(c) || c === "_";
        };
        var identifier = this.input[this.currentIndex];
        this.currentIndex++;
        while (this.currentIndex < this.input.length && isValidIdentifier(this.input[this.currentIndex])) {
            identifier += this.input[this.currentIndex];
            this.currentIndex++;
        }
        var isReserved = Parser.isReserved(identifier.toLowerCase());
        // If the identifier is followed by an opening paren, it's a function call.
        // Otherwise, it's a variable lookup.
        if (this.currentIndex < this.input.length && this.input[this.currentIndex] === '(') {
            if (isReserved) {
                throw this.parseError('Cannot invoke reserved keyword as function');
            }
            var params = this.parseFunctionParameters(identifier);
            if (!params) {
                throw this.parseError('Expected to find function parameters after opening parenthesis');
            }
            return this.create(node_1.FunctionCallNode, identifier, params);
        }
        else if (['true', 'false'].includes(identifier.toLowerCase())) {
            return this.create(node_1.BooleanLiteralNode, identifier.toLowerCase() === 'true');
        }
        else if (isReserved) {
            throw this.parseError("Keyword '" + identifier + "' is reserved");
        }
        else {
            return this.create(node_1.VariableLookupNode, identifier);
        }
    };
    Parser.prototype.parseFunctionParameters = function (identifier) {
        if (this.currentIndex >= this.input.length || this.input[this.currentIndex] !== "(")
            return null;
        // Consume opening paren
        this.currentIndex++;
        var parameters = [];
        this.states.push("function");
        while (this.currentIndex < this.input.length && this.input[this.currentIndex] !== ")") {
            var param = this.parseExpression();
            if (!param) {
                throw this.parseError("Expected to find expression for parameter to '" + identifier + "' function");
            }
            parameters.push(param);
            this.skipWhitespace();
            if (this.input[this.currentIndex] === ",") {
                // Consume comma after param
                this.currentIndex++;
                this.skipWhitespace();
                if (this.currentIndex >= this.input.length || this.input[this.currentIndex] === ")") {
                    throw this.parseError("Expected to find function parameter after comma");
                }
            }
        }
        if (this.currentIndex >= this.input.length || this.input[this.currentIndex] !== ")") {
            throw this.parseError("Expected to find closing parenthesis after arguments to '" + identifier + "' function");
        }
        // Consume closing paren
        this.currentIndex++;
        this.states.pop();
        return parameters;
    };
    Parser.prototype.parseString = function () {
        if (this.currentIndex >= this.input.length)
            return null;
        var quote = this.input[this.currentIndex];
        this.currentIndex++;
        var value = '';
        var terminated = false;
        var prevChar = null;
        while (this.currentIndex < this.input.length) {
            var c = this.input[this.currentIndex];
            // Allow backslash escaped quotes matching
            if (c === quote) {
                if (prevChar === "\\") {
                    // Remove the backslash and replace it with the quote character
                    value = value.slice(0, -1) + c;
                    this.currentIndex++;
                    // Continue on to the next iteration since we've already consumed the current character.
                    continue;
                }
                else {
                    // Terminate the string upon finding a matching quote
                    terminated = true;
                    // Advance past the closing quote
                    this.currentIndex++;
                    break;
                }
            }
            // Consume the current character
            value += c;
            prevChar = c;
            this.currentIndex++;
        }
        if (!terminated) {
            throw this.parseError('Unterminated string');
        }
        return this.create(node_1.StringLiteralNode, value);
    };
    Parser.prototype.parseUnaryMinus = function () {
        if (this.currentIndex >= this.input.length || this.input[this.currentIndex] !== "-")
            return null;
        // Consume '-' character
        this.currentIndex++;
        var node = this.parseExpressionStart();
        if (!node) {
            throw this.parseError("Expected expression after unary minus");
        }
        return this.create(node_1.UnaryMinusNode, node);
    };
    Parser.prototype.parseBinaryOperator = function (left) {
        if (this.currentIndex >= this.input.length)
            return null;
        var operatorStr = this.input[this.currentIndex];
        // Advance past operator character
        this.currentIndex++;
        // handle two character comparison operators
        if (this.currentIndex < this.input.length && (operatorStr === ">" || operatorStr === "<")) {
            var nextChar = this.input[this.currentIndex];
            // If the char after the first operator char forms a valid operator, use that.
            if (binaryOperator_1.getBinaryOperator(operatorStr + nextChar) !== null) {
                operatorStr += nextChar;
                // Advance past second char
                this.currentIndex++;
            }
        }
        var operator = binaryOperator_1.getBinaryOperator(operatorStr);
        if (!operator) {
            throw this.parseError("Invalid operator '" + operatorStr + "'");
        }
        var right = this.parseExpression();
        if (!right) {
            throw this.parseError("Expected expression on right hand side of operator '" + operatorStr + "'");
        }
        // If the right hand side of this operator is another binary operator with a lower precedence than this operator,
        // e.g. a * b + c, we currently have left = a and right = (b + c), which would produce a * (b + c) which is
        // incorrect. We need to replace left with (a * b) and right with c. In more abstract terms: left becomes (old
        // left, operator 1, old right's left) and right becomes the old right's right.
        //
        // More visually, the following change is made to the node tree:
        //    *                            +
        //  /   \                        /   \
        // a     +        =====>        *     c
        //     /   \                  /  \
        //   b       c              a     b
        //
        // We also do the same thing for equal operators, in order to follow left-associativity. Meaning that
        // '1 - 2 - 3' should be parsed as equivalent to '(1 - 2) - 3' and not '1 - (2 - 3)' since they have different
        // results (-4 and 2, respectively).
        if (right instanceof node_1.BinaryOperatorNode && right.operator <= operator) {
            // Manually construct a new node here instead of using this.create, because we set the sourceStart/sourceEnd ourselves
            // after tree-rebalancing
            var newLeft = new node_1.BinaryOperatorNode(left, operator, right.left);
            newLeft.sourceStart = newLeft.left.sourceStart;
            newLeft.sourceEnd = newLeft.right.sourceEnd;
            var newRight = right.right;
            // don't need to adjust source indices, they'll already be correct when right.right was originally parsed.
            // again, construct the node ourselves and set the source indices
            var outer = new node_1.BinaryOperatorNode(newLeft, right.operator, newRight);
            outer.sourceStart = outer.left.sourceStart;
            outer.sourceEnd = outer.right.sourceEnd;
            return outer;
        }
        else {
            // If the right is not a binary operator, or the right precedence is less than our own precedence,
            // things are already in the correct order and we can just construct a new AST node without shuffling things around.
            // We construct the node ourselves here as well in order to set the source indices. When parseBinaryOperator is invoked
            // the source start index for the left node will already have been popped from the nodeStartIndices array, so we need
            // to set it ourselves from the left node.
            var node = new node_1.BinaryOperatorNode(left, operator, right);
            node.sourceStart = node.left.sourceStart;
            node.sourceEnd = node.right.sourceEnd;
            return node;
        }
    };
    Parser.prototype.parseGroup = function () {
        if (this.currentIndex >= this.input.length || this.input[this.currentIndex] !== "(")
            return null;
        // Advance past opening paren
        this.currentIndex++;
        // Save the current group parsing state, so that if we're in a nested group, we don't prematurely move out of the group parsing state.
        this.states.push("group");
        var content = this.parseExpression();
        this.states.pop();
        if (!content) {
            throw this.parseError('Expected to find expression inside of grouping parentheses');
        }
        this.skipWhitespace();
        if (this.input[this.currentIndex] !== ")") {
            throw this.parseError("Expected closing parenthesis after group");
        }
        // Advance past closing paren
        this.currentIndex++;
        return this.create(node_1.GroupNode, content);
    };
    Parser.prototype.parseArray = function () {
        if (!this.parseArrayLiterals || this.currentIndex >= this.input.length || this.input[this.currentIndex] !== "[")
            return null;
        // Consume opening brace
        this.currentIndex++;
        var elements = [];
        // Save the current array state, so that if we're in a nested array, we don't move out of it prematurely
        this.states.push("array");
        while (this.currentIndex < this.input.length && this.input[this.currentIndex] !== "]") {
            var elem = this.parseExpression();
            if (!elem) {
                throw this.parseError("Expected to find array element");
            }
            elements.push(elem);
            this.skipWhitespace();
            if (this.input[this.currentIndex] === ",") {
                // Consume comma after value
                this.currentIndex++;
                this.skipWhitespace();
                if (this.currentIndex >= this.input.length || this.input[this.currentIndex] === "]") {
                    throw this.parseError("Expected to find array element after comma");
                }
            }
        }
        if (this.currentIndex >= this.input.length || this.input[this.currentIndex] !== "]") {
            throw this.parseError("Expected to find closing brace after array elements");
        }
        // Consume closing brace
        this.currentIndex++;
        this.states.pop();
        return this.create(node_1.ArrayLiteralNode, elements);
    };
    /**
     * Helper function that create a new AST node of the given type.
     *
     * This function should be used when parsing instead of constructing nodes directly because it automatically sets
     * the {@link ASTNode.sourceStart} property on the constructed node to the current index of the parser.
     *
     * @param ctor The actual constructor of the given AST node type.
     * @param params The parameters to pass to the constructor
     * @typeParam T The type of AST node to construct.
     */
    Parser.prototype.create = function (ctor) {
        var params = [];
        for (var _i = 1; _i < arguments.length; _i++) {
            params[_i - 1] = arguments[_i];
        }
        var node = new (ctor.bind.apply(ctor, __spreadArrays([void 0], params)))();
        var start = this.nodeStartIndices.pop();
        if (typeof start !== "number") {
            throw new Error('Invalid internal Parser state, missing node start index');
        }
        node.sourceStart = start;
        node.sourceEnd = this.currentIndex;
        return node;
    };
    Parser.prototype.skipWhitespace = function () {
        while (this.currentIndex < this.input.length && isSpace(this.input[this.currentIndex])) {
            this.currentIndex++;
        }
    };
    Parser.prototype.parseError = function (message) {
        return new parseError_1.default(message, this.currentIndex);
    };
    /**
     * Check if the given identifier is a reserved keyword.
     * @param identifier The identifier to check
     */
    Parser.isReserved = function (identifier) {
        return Parser.reservedIdentifiers.includes(identifier);
    };
    /**
     * Identifiers that can occur in formulas but are reserved for use by the engine and may not be used as function or
     * variable names.
     */
    Parser.reservedIdentifiers = [
        'true',
        'false',
    ];
    return Parser;
}());
exports.Parser = Parser;
function isAlpha(c) {
    var code = c.charCodeAt(0);
    return (code >= 65 && code <= 90) || (code >= 97 && code <= 122);
}
function isDigit(c) {
    var code = c.charCodeAt(0);
    return code >= 48 && code <= 57;
}
function isAlphaNum(c) {
    return isAlpha(c) || isDigit(c);
}
function isSpace(c) {
    return c === " " || c === "\t" || c === "\r" || c === "\n" || c === "\u00A0";
}
//# sourceMappingURL=parser.js.map